import { ChevronDown, ChevronRight, MessageCircle, Settings } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionCard } from "../components";
import { useSettingsUIStore } from "../store/use-settings-ui-store";
import { getCurrentSectionFromUrl, groupSectionsByCategory } from "../utils";
import { useCategoryInfo, useSettingsSections } from "../utils/constants";

const SUPPORT_WHATSAPP_URL = "https://wa.me/4915231480096";

export function SettingsNavigationSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("settings");

  const { activeSection, expandedCategories, setActiveSection, toggleCategory, expandCategory } =
    useSettingsUIStore();

  const settingsSections = useSettingsSections();
  const categoryInfo = useCategoryInfo();

  // Update active section when URL changes
  useEffect(() => {
    const section = getCurrentSectionFromUrl(location.pathname);
    setActiveSection(section);

    // Auto-expand the category containing the active section
    const activeSettingsSection = settingsSections.find((s) => s.id === section);
    if (activeSettingsSection) {
      expandCategory(activeSettingsSection.category);
    }
  }, [location.pathname, setActiveSection, expandCategory, settingsSections]);

  const selectSection = (sectionId: string) => {
    navigate(`/settings/${sectionId}`);
  };

  const groupedSections = groupSectionsByCategory(settingsSections);

  return (
    <div className="w-full h-full flex flex-col py-2">
      <div className="px-2 h-full flex flex-col">
        <Card className="h-full flex flex-col">
          <CardContent className="p-3 flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <Settings className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                <h2 className="font-semibold text-xl">{t("title")}</h2>
              </div>
            </div>

            {/* Navigation Categories */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <nav className="space-y-1 pr-2" aria-label="Settings navigation">
                {Object.entries(categoryInfo).map(([categoryKey, category]) => {
                  const categoryItems = groupedSections[categoryKey] || [];
                  if (categoryItems.length === 0) return null;

                  const isExpanded = expandedCategories.has(categoryKey);

                  return (
                    <div key={categoryKey} className="space-y-1">
                      <button
                        type="button"
                        onClick={() => toggleCategory(categoryKey)}
                        className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-accent transition-colors"
                      >
                        <div className="text-left">
                          <div className="font-medium text-sm text-foreground">
                            {category.title}
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="space-y-1 pl-2">
                          {categoryItems.map((section) => (
                            <SectionCard
                              key={section.id}
                              section={section}
                              isActive={activeSection === section.id}
                              onClick={() => selectSection(section.id)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>

            {/* Contact Support */}
            <div className="mt-3 pt-3 border-t flex-shrink-0">
              <Button asChild variant="outline" size="lg" className="w-full h-12 text-lg">
                <a
                  href={SUPPORT_WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3"
                >
                  <MessageCircle className="w-6 h-6" />
                  <span>{t("contactSupport")}</span>
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
